/**
 * CacheArrayOperations - Array-specific cache operations
 * Extracted from OrganizationCache to follow Single Responsibility Principle
 */

export class CacheArrayOperations {
  constructor(cacheManager) {
    this.manager = cacheManager;
  }

  /**
   * Remove specific item from cached array by identifier
   * @param {string} dataType - Type of data
   * @param {string} orgId - Organization ID
   * @param {string} itemIdentifier - Item identifier to remove
   * @param {string} identifierField - Field name to match (default: 'notificationId')
   * @param {string} subKey - Optional sub-key
   * @returns {boolean} True if item was removed
   */
  removeFromCachedArray(dataType, orgId, itemIdentifier, identifierField = 'notificationId', subKey = '') {
    const key = this.manager.getCacheKey(orgId, dataType, subKey);

    if (this.manager.cache[dataType] &&
        this.manager.cache[dataType][key] &&
        Array.isArray(this.manager.cache[dataType][key])) {

      const originalLength = this.manager.cache[dataType][key].length;

      // Filter out the item with matching identifier (check multiple possible fields)
      this.manager.cache[dataType][key] = this.manager.cache[dataType][key].filter(item => {
        return item[identifierField] !== itemIdentifier &&
               item.notificationId !== itemIdentifier &&
               item.invitationId !== itemIdentifier &&
               item.token !== itemIdentifier &&
               item.id !== itemIdentifier;
      });

      const newLength = this.manager.cache[dataType][key].length;

      if (originalLength !== newLength) {
        this.manager.expiryTimes[dataType][key] = Date.now() + this.manager.TTL[dataType];
        this.manager.scheduleSave();
        return true;
      }
    }

    return false;
  }

  /**
   * Add item to cached array
   * @param {string} dataType - Type of data
   * @param {string} orgId - Organization ID
   * @param {Object} newItem - Item to add
   * @param {string} subKey - Optional sub-key
   * @returns {boolean} True if item was added
   */
  addToCachedArray(dataType, orgId, newItem, subKey = '') {
    const key = this.manager.getCacheKey(orgId, dataType, subKey);

    if (this.manager.cache[dataType] &&
        this.manager.cache[dataType][key] &&
        Array.isArray(this.manager.cache[dataType][key])) {

      // For notifications, add to the beginning (newest first)
      if (dataType === 'notifications') {
        this.manager.cache[dataType][key].unshift(newItem);
      } else {
        this.manager.cache[dataType][key].push(newItem);
      }

      this.manager.expiryTimes[dataType][key] = Date.now() + this.manager.TTL[dataType];
      this.manager.scheduleSave();
      return true;
    }

    return false;
  }

  /**
   * Update item in cached array
   * @param {string} dataType - Type of data
   * @param {string} orgId - Organization ID
   * @param {string} itemIdentifier - Item identifier to update
   * @param {Object} updatedItem - Updated item data (partial)
   * @param {string} identifierField - Field name to match (default: 'notificationId')
   * @param {string} subKey - Optional sub-key
   * @returns {boolean} True if item was updated
   */
  updateInCachedArray(dataType, orgId, itemIdentifier, updatedItem, identifierField = 'notificationId', subKey = '') {
    const key = this.manager.getCacheKey(orgId, dataType, subKey);

    if (this.manager.cache[dataType] &&
        this.manager.cache[dataType][key] &&
        Array.isArray(this.manager.cache[dataType][key])) {

      const itemIndex = this.manager.cache[dataType][key].findIndex(item =>
        item[identifierField] === itemIdentifier
      );

      if (itemIndex !== -1) {
        this.manager.cache[dataType][key][itemIndex] = {
          ...this.manager.cache[dataType][key][itemIndex],
          ...updatedItem
        };
        this.manager.expiryTimes[dataType][key] = Date.now() + this.manager.TTL[dataType];
        this.manager.scheduleSave();
        return true;
      }
    }

    return false;
  }

  /**
   * Mark notification as read in cache
   * @param {string} userId - User ID
   * @param {string} notificationId - Notification ID
   * @returns {boolean} True if notification was marked as read
   */
  markNotificationAsRead(userId, notificationId) {
    return this.updateInCachedArray('notifications', userId, notificationId, { read: true }, 'notificationId');
  }

  /**
   * Mark all notifications as read in cache
   * @param {string} userId - User ID
   * @returns {boolean} True if all notifications were marked as read
   */
  markAllNotificationsAsRead(userId) {
    const key = this.manager.getCacheKey(userId, 'notifications');

    if (this.manager.cache.notifications &&
        this.manager.cache.notifications[key] &&
        Array.isArray(this.manager.cache.notifications[key])) {

      this.manager.cache.notifications[key] = this.manager.cache.notifications[key].map(notification => ({
        ...notification,
        read: true
      }));

      this.manager.expiryTimes.notifications[key] = Date.now() + this.manager.TTL.notifications;
      this.manager.scheduleSave();
      return true;
    }

    return false;
  }
}
